<template>
  <div class="sp-root">
    <!-- RESULTADO -->
    <div class="sp-hero" v-if="resultado && !resultado.erro">
      <div class="sp-hero-valor">{{ formatNum(resultado.resultado) }}</div>
    </div>
    <div class="sp-hero sp-hero-erro" v-else-if="resultado && resultado.erro">
      <fa-icon icon="exclamation-triangle" />
      <span>Erro na linha {{ resultado.linhaErro }}</span>
    </div>
    <div class="sp-hero sp-hero-empty" v-else>
      <span>Monte a fórmula</span>
    </div>

    <!-- ETAPAS INTERMEDIÁRIAS -->
    <div class="sp-steps" v-if="resultado && !resultado.erro && Object.keys(resultado.intermediarios).length > 0">
      <div class="sp-section-label">ETAPAS</div>
      <div
        v-for="(valor, nome) in resultado.intermediarios"
        :key="nome"
        class="sp-step"
        :title="'Arraste para usar ' + nome + ' em outra etapa'"
        draggable="true"
        @dragstart="onDrag($event, nome)"
      >
        <div class="sp-step-box">
          <span class="sp-step-initials">{{ getInitials(variaveisMap[nome] || nome) }}</span>
        </div>
        <div class="sp-step-info">
          <span class="sp-step-name">{{ variaveisMap[nome] || nome }}</span>
          <span class="sp-step-val">{{ formatNum(valor) }}</span>
        </div>
      </div>
    </div>

    <!-- VALORES DE SIMULAÇÃO - ITEM -->
    <div class="sp-section-label" v-if="variaveisItemOrdenadas.length > 0">SIMULAÇÃO — ITEM</div>
    <div class="sp-vars" v-if="variaveisItemOrdenadas.length > 0">
      <div
        v-for="v in variaveisItemOrdenadas"
        :key="v.chave"
        class="sp-var"
        :class="{ 'sp-var-usada': v.usada }"
        :title="v.nome"
        draggable="true"
        @dragstart="onDrag($event, v.chave)"
      >
        <div class="sp-var-node" :class="'sp-var-' + v.tipo">
          {{ getInitials(v.nome) }}
        </div>
        <span class="sp-var-name" :title="v.nome">{{ v.nome }}</span>
        <input
          type="number"
          :value="valores[v.chave]"
          @input="$emit('update-valor', v.chave, +$event.target.value)"
          step="any"
        />
      </div>
    </div>

    <!-- VALORES DE SIMULAÇÃO - ORÇAMENTO -->
    <div class="sp-section-label" v-if="variaveisOrcamentoOrdenadas.length > 0">SIMULAÇÃO — ORÇAMENTO</div>
    <div class="sp-vars" v-if="variaveisOrcamentoOrdenadas.length > 0">
      <div
        v-for="v in variaveisOrcamentoOrdenadas"
        :key="v.chave"
        class="sp-var"
        :class="{ 'sp-var-usada': v.usada }"
        :title="v.nome"
        draggable="true"
        @dragstart="onDrag($event, v.chave)"
      >
        <div class="sp-var-node" :class="'sp-var-' + v.tipo">
          {{ getInitials(v.nome) }}
        </div>
        <span class="sp-var-name" :title="v.nome">{{ v.nome }}</span>
        <input
          type="number"
          :value="valores[v.chave]"
          @input="$emit('update-valor', v.chave, +$event.target.value)"
          step="any"
        />
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "SimuladorPreview",
  props: {
    variaveis: { type: Array, default: () => [] },
    valores: { type: Object, default: () => ({}) },
    resultado: { type: Object, default: null },
    resultadoFinal: { type: String, default: "" },
    variaveisUsadas: { type: Array, default: () => [] },
    variaveisMap: { type: Object, default: () => ({}) },
  },
  computed: {
    variaveisOrdenadas() {
      const usadasSet = new Set(this.variaveisUsadas);
      return [...this.variaveis]
        .map((v) => ({ ...v, usada: usadasSet.has(v.chave) }))
        .sort((a, b) => {
          if (a.usada && !b.usada) return -1;
          if (!a.usada && b.usada) return 1;
          return 0;
        });
    },
    variaveisItemOrdenadas() {
      return this.variaveisOrdenadas.filter((v) => v.escopo !== 'orcamento');
    },
    variaveisOrcamentoOrdenadas() {
      return this.variaveisOrdenadas.filter((v) => v.escopo === 'orcamento');
    },
  },
  methods: {
    formatNum(v) {
      if (v === null || v === undefined) return "—";
      return Number(v).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 4 });
    },
    onDrag(e, chave) {
      e.dataTransfer.setData("text/plain", chave);
      e.dataTransfer.effectAllowed = "copy";
    },
    getInitials(nome) {
      const words = nome.split(/[\s_]+/).filter(Boolean);
      const stopWords = ['de', 'do', 'da', 'dos', 'das', 'os', 'as', 'o', 'a', 'com', 'sem', 'em', 'no', 'na', 'e', 'ou'];
      const meaningful = words.filter((w) => !stopWords.includes(w.toLowerCase()));
      if (meaningful.length >= 3) {
        return (meaningful[0][0] + meaningful[meaningful.length - 1][0]).toUpperCase();
      }
      if (meaningful.length === 2) return (meaningful[0][0] + meaningful[1][0]).toUpperCase();
      if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
      return nome.substring(0, 2).toUpperCase();
    },
  },
};
</script>

<style scoped>
.sp-root { display: flex; flex-direction: column; gap: 10px; }
.sp-section-label {
  font-size: 0.55rem; font-weight: 800; letter-spacing: 1.5px;
  color: #9ca3af;
}

/* RESULTADO */
.sp-hero { padding: 14px; border-radius: 12px; text-align: center; }
.sp-hero:not(.sp-hero-erro):not(.sp-hero-empty) {
  background: linear-gradient(135deg, #ecfdf5, #d1fae5);
  border: 1.5px solid #a7f3d0;
}
.sp-hero-valor {
  font-family: 'SF Mono', monospace;
  font-size: 1.8rem; font-weight: 800; color: #059669;
}
.sp-hero-erro {
  background: #fef2f2; border: 1.5px solid #fecaca;
  color: #dc2626; font-size: 0.78rem; font-weight: 600;
  display: flex; align-items: center; justify-content: center; gap: 8px;
}
.sp-hero-empty {
  background: #f9fafb; border: 1.5px dashed #d1d5db;
  color: #9ca3af; font-size: 0.78rem;
}

/* ETAPAS */
.sp-steps { display: flex; flex-direction: column; gap: 4px; }
.sp-step {
  display: flex; align-items: center; gap: 8px;
  padding: 4px; border-radius: 8px;
  transition: background 0.15s;
  cursor: grab;
}
.sp-step:active { cursor: grabbing; }
.sp-step:hover { background: #f0f4ff; }
.sp-step-box {
  width: 28px; height: 28px; border-radius: 7px;
  display: flex; align-items: center; justify-content: center;
  background: linear-gradient(135deg, #eef2ff, #e0e7ff);
  border: 1px solid #c7d2fe;
  flex-shrink: 0;
}
.sp-step-initials { font-size: 0.55rem; font-weight: 800; color: #4f46e5; }
.sp-step-info { display: flex; flex-direction: column; flex: 1; min-width: 0; }
.sp-step-name {
  font-size: 0.62rem; color: #9ca3af;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.sp-step-val {
  font-family: 'SF Mono', monospace; font-size: 0.7rem;
  color: #374151; font-weight: 600;
}

/* SIMULAÇÃO */
.sp-vars { display: flex; flex-direction: column; gap: 4px; }
.sp-var {
  display: flex; align-items: center; gap: 6px;
  padding: 3px; border-radius: 8px;
  transition: background 0.15s; cursor: grab;
}
.sp-var:active { cursor: grabbing; }
.sp-var-usada { background: #f0fdf4; }
.sp-var-node {
  width: 30px; height: 30px; border-radius: 8px;
  display: flex; align-items: center; justify-content: center;
  font-size: 0.55rem; font-weight: 800;
  flex-shrink: 0;
  border: 1.5px solid #e5e7eb; background: #fff;
}
.sp-var-valor { border-color: #a7f3d0; background: linear-gradient(135deg, #ecfdf5, #d1fae5); color: #065f46; }
.sp-var-percentual { border-color: #fdba74; background: linear-gradient(135deg, #fff7ed, #ffedd5); color: #9a3412; }
.sp-var-quantidade { border-color: #93c5fd; background: linear-gradient(135deg, #eff6ff, #dbeafe); color: #1e40af; }
.sp-var-fator { border-color: #c4b5fd; background: linear-gradient(135deg, #f5f3ff, #ede9fe); color: #5b21b6; }
.sp-var-name {
  font-size: 0.65rem; color: #6b7280; flex: 1; min-width: 0;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  max-width: 120px;
}
.sp-var-usada .sp-var-name { color: #374151; font-weight: 500; }
.sp-var input {
  width: 60px; border: 1px solid #e5e7eb;
  background: #fff; border-radius: 6px;
  padding: 3px 5px; font-size: 0.68rem;
  font-family: 'SF Mono', monospace; color: #374151;
  text-align: right; outline: none; flex-shrink: 0;
}
.sp-var input:focus { border-color: #818cf8; }
</style>
